import { LEAD_CAPTURE_FIELDS } from './leadCaptureFields.js';

export const ANSWER_LABEL_MAX_LENGTH = 120;
const UUID = /^[a-f0-9]{8}-[a-f0-9]{4}-[1-8][a-f0-9]{3}-[89ab][a-f0-9]{3}-[a-f0-9]{12}$/;
const ADDITIONAL_KEY = /^additional_[a-f0-9]{32}$/;
const own = (value, key) => Object.prototype.hasOwnProperty.call(value, key);
const plain = value => value && typeof value === 'object' && !Array.isArray(value);
const bounded = text => {
  if (text.length <= ANSWER_LABEL_MAX_LENGTH) return text;
  let result = '';
  for (const character of text) {
    if (result.length + character.length > ANSWER_LABEL_MAX_LENGTH - 1) break;
    result += character;
  }
  return `${result.trimEnd()}…`;
};

export function additionalAnswerKey(id) {
  if (typeof id !== 'string' || !UUID.test(id)) throw new TypeError('A valid lowercase answer UUID is required.');
  return `additional_${id.replaceAll('-', '')}`;
}

export function automaticAnswerLabel(step) {
  const columns = Array.isArray(step?.step_columns) ? step.step_columns : [];
  const field = columns.length === 1 && LEAD_CAPTURE_FIELDS.find(item => item.value === columns[0]);
  if (field) return field.value === 'name' ? 'Full name' : field.label;
  const prompt = (Array.isArray(step?.step_prompts) ? step.step_prompts : [])
    .filter(value => typeof value === 'string' && value.trim()).at(-1);
  return bounded(prompt?.replace(/\s+/gu, ' ').trim() || 'Additional answer');
}

export function createAdditionalAnswer(step, id = globalThis.crypto?.randomUUID?.()) {
  const key = additionalAnswerKey(id);
  return { key, metadata: { version: 1, id, label: automaticAnswerLabel({ ...step, step_columns: [key] }), label_mode: 'auto' } };
}

// Legacy steps without metadata keep their keys and identity, including unusual
// existing keys. Metadata labels only one retained answer and never rekeys it.
export function validateStepAnswer(step) {
  if (!own(step || {}, 'step_answer')) return null;
  const meta = step.step_answer;
  const invalid = message => ({ code: 'invalid_answer_metadata', message, field: 'field' });
  if (!plain(meta) || meta.version !== 1 || !['auto', 'custom'].includes(meta.label_mode) ||
    Object.keys(meta).some(key => !['version', 'id', 'label', 'label_mode'].includes(key))) return invalid('use supported answer settings.');
  if (typeof meta.label !== 'string' || !meta.label.trim() || meta.label !== meta.label.trim() ||
    meta.label.length > ANSWER_LABEL_MAX_LENGTH || /[\u0000-\u001f\u007f]/u.test(meta.label)) {
    return { ...invalid('use an answer label of 1–120 characters without line breaks.'), field: 'answer_label' };
  }
  if (step.step_type === 'message' || !Array.isArray(step.step_columns) || step.step_columns.length !== 1) {
    return invalid('answer labels require exactly one saved answer.');
  }
  const key = step.step_columns[0];
  if (ADDITIONAL_KEY.test(key)) {
    if (!UUID.test(meta.id || '') || additionalAnswerKey(meta.id) !== key) return invalid('the saved answer identity does not match its storage key.');
  } else if (own(meta, 'id')) return invalid('existing answer fields must keep their original identity.');
  return null;
}

export function answerLabelForStep(step) {
  const meta = step?.step_answer;
  if (meta && !validateStepAnswer(step)) return meta.label_mode === 'auto' ? automaticAnswerLabel(step) : meta.label;
  return automaticAnswerLabel(step);
}
